import { useRef } from 'react';
import { VideoQuality } from '@zoom/videosdk';
import { HandRaisedIcon } from '@heroicons/react/24/solid';
import { useAttachedVideo } from '../../../hooks/useAttachedVideo';
import { useInView } from '../../../hooks/useInView';

const getInitials = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const QUALITY_BY_VARIANT = {
    stage: VideoQuality.Video_720P,
    compact: VideoQuality.Video_180P,
    default: VideoQuality.Video_360P,
};

const MicBadge = ({ muted }) => (
    <span className={`vc-mic ${muted ? 'is-muted' : ''}`}>
        <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">
            {muted ? (
                <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3 3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
            ) : (
                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5-3h-1.7c0 3-2.54 5.1-5.3 5.1S4.7 14 4.7 11H3c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72z" />
            )}
        </svg>
    </span>
);

/**
 * Recuadro de video de un participante.
 *
 * La calidad se fija en el primer render: cambiarla obliga a desadjuntar y
 * readjuntar el stream, y eso rompía el video al iniciar screen share.
 * El tamaño se controla solo con CSS.
 */
export const VideoParticipantTile = ({
    participant,
    variant = 'default',
    isActiveSpeaker = false,
    handRaised = false,
    /** Solo adjunta el video mientras el recuadro está a la vista (listas con scroll). */
    lazy = false,
    className = '',
}) => {
    const rootRef = useRef(null);
    const shellRef = useRef(null);
    const qualityRef = useRef(QUALITY_BY_VARIANT[variant] ?? QUALITY_BY_VARIANT.default);
    const compact = variant === 'compact';
    const inView = useInView(rootRef, { enabled: lazy });
    const videoOn = Boolean(participant?.bVideoOn) && inView;

    useAttachedVideo(shellRef, participant?.userId, {
        enabled: videoOn,
        quality: qualityRef.current,
        className: 'vc-tile__player',
    });

    if (!participant) return null;

    return (
        <div
            ref={rootRef}
            className={[
                'vc-tile',
                compact ? 'vc-tile--compact' : '',
                variant === 'stage' ? 'vc-tile--stage' : '',
                isActiveSpeaker ? 'is-speaking' : '',
                handRaised ? 'has-hand' : '',
                className,
            ].filter(Boolean).join(' ')}
        >
            <div
                ref={shellRef}
                className={`vc-tile__video ${videoOn ? 'is-on' : 'is-off'}`}
            />
            {handRaised ? (
                <span className="vc-tile__hand" title="Mano levantada">
                    <HandRaisedIcon aria-hidden="true" />
                    <span className="sr-only">Mano levantada</span>
                </span>
            ) : null}
            {!videoOn && (
                <div className="vc-tile__avatar" data-size={compact ? 'sm' : 'lg'}>
                    {getInitials(participant.displayName)}
                </div>
            )}
            <span className="vc-tile__name">
                {participant.displayName}
                {participant.isSelf ? ' (Tú)' : ''}
            </span>
            <MicBadge muted={participant.muted} />
        </div>
    );
};
